// Les URLs de fallback des cards Algolia sont générées côté JS et doivent
// respecter le préfixe de langue (ex. "/en") sous peine de renvoyer
// systématiquement vers la fiche produit en français sur les sites étrangers.
function productUrl(handle) {
  const root = theme.routes?.root_url?.replace(/\/$/, "") || "";
  return `${root}/products/${handle}`;
}

function handleize(str) {
  return str
    .toString()
    .normalize("NFD") // sépare les accents des lettres
    .replace(/[\u0300-\u036f]/g, "") // supprime les accents
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "-") // remplace tout caractère non alphanumérique par un tiret
    .replace(/[\s_]+/g, "-") // remplace espaces/underscores par un tiret
    .replace(/-+/g, "-") // fusionne les tirets multiples
    .replace(/^-+|-+$/g, ""); // retire les tirets en début/fin
}

// Les traductions éditées depuis l'admin Shopify (Translate & Adapt) peuvent
// revenir avec des entités HTML (ex. "&#39;" pour une apostrophe) même sur des
// clés texte brut : on les décode ici pour éviter de les afficher telles quelles.
function decodeHtmlEntities(str) {
  return new DOMParser().parseFromString(str, "text/html").documentElement
    .textContent;
}

// L'app Algolia ne peuple pas forcément algoliaShopify.translations au moment
// où algolia.hooks.initialize se déclenche : on ne peut merger nos clés qu'une
// fois à l'intérieur des hooks "point d'entrée" (InstantSearch / Autocomplete),
// d'où cette fonction idempotente appelée à chaque endroit qui en dépend.
function ensureAlgoliaTranslations() {
  algoliaShopify.translations = Object.assign(
    algoliaShopify.translations || {},
    window.theme?.algoliaStrings,
  );

  for (const [key, value] of Object.entries(algoliaShopify.translations)) {
    if (typeof value === "string") {
      algoliaShopify.translations[key] = decodeHtmlEntities(value);
    }
  }

  algoliaShopify.translations.facetLabels = Object.fromEntries(
    Object.entries(window.theme?.algoliaFacetLabels || {}).map(
      ([name, label]) => [name, decodeHtmlEntities(label)],
    ),
  );
}

// Les libellés de tri viennent d'Algolia : on les traduit à partir du nom d'index avec par défaut le libellé d'origine.
function translateSortOrder(order) {
  const t = algoliaShopify.translations;
  const name = order.value || "";
  let label = order.label;

  if (order.label === "Relevance") label = t.relevance;
  else if (/_recently_ordered_count_desc$/.test(name))
    label = t.sortBestSelling;
  else if (/_price_ratio_asc$/.test(name)) label = t.sortPriceRatio;
  else if (/_price_asc$/.test(name)) label = t.sortPriceAsc;
  else if (/_published_at_desc$/.test(name)) label = t.sortNewest;

  return { ...order, label: label || order.label };
}

// Certains facets (ex. meta.custom.*) sont des booléens indexés côté Algolia
// comme les chaînes "true"/"false" : on les affiche traduits (Oui/Non) plutôt
// que bruts dans les listes de filtres.
function translateFacetValueLabel(label) {
  if (label === "true") return algoliaShopify.translations.booleanYes;
  if (label === "false") return algoliaShopify.translations.booleanNo;
  return label;
}

// Réplique de snippets/get-best-price.liquid côté client pour les cards
// Algolia : le hit ne connaît que le produit, pas le client courant, d'où
// window.theme.customerPricing (exposé par snippets/js-variables.liquid) pour
// la partie revendeur/club.
const RESELLER_GRADE_METAFIELD_KEYS = {
  Or: "prix_revendeur_or",
  Argent: "prix_revendeur_argent",
  Bronze: "prix_revendeur_bronze",
};

const CLUB_EXCLUDED_PASTILLES = [
  "Exclusion permanente",
  "Exclusion temporaire",
  "Exclusion marge",
];
const CLUB_BASE_RATE = 0.8;

function parseMoneyMetafield(raw) {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const amount = parseFloat(parsed.amount);
    return Number.isNaN(amount) ? null : amount;
  } catch (e) {
    return null;
  }
}

// Réappro prévu : même règle que snippets/restocking-status.liquid
// (rupture + custom.restocking_quantity > 0 + custom.restocking_date >= aujourd'hui).
function isRestocking(hit) {
  if (hit.inventory_available) return false;
  const quantity = Number(hit.meta?.custom?.restocking_quantity);
  const date = hit.meta?.custom?.restocking_date;
  if (!(quantity > 0) || !date) return false;

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return String(date).slice(0, 10) >= today;
}

function getResellerPrice(hit) {
  const grade = window.theme?.customerPricing?.gradeRevendeur;
  const metafieldKey = grade && RESELLER_GRADE_METAFIELD_KEYS[grade];
  if (!metafieldKey) return null;
  return parseMoneyMetafield(hit.meta?.custom?.[metafieldKey]);
}

function getClubPrice(hit, basePrice) {
  const club = window.theme?.customerPricing?.club;
  if (!club) return null;

  const pastille = hit.meta?.akeneo?.pastille;
  if (CLUB_EXCLUDED_PASTILLES.includes(pastille)) return null;

  const productCollectionIds = hit.collection_ids || [];
  let rate = CLUB_BASE_RATE;

  for (let i = 0; i < club.collectionIds.length; i++) {
    const collectionId = club.collectionIds[i];
    if (collectionId && productCollectionIds.includes(collectionId)) {
      rate = (100 - Number(club.rates[i])) / 100;
      break;
    }
  }

  return basePrice * rate;
}

function getBulkTiers(hit) {
  const raw = hit.meta?.custom?.bulk_discounts;
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Object.entries(parsed)
      .map(([qty, price]) => ({
        qty: parseInt(qty, 10),
        price: parseFloat(price),
      }))
      .filter((tier) => !Number.isNaN(tier.qty) && !Number.isNaN(tier.price))
      .sort((a, b) => a.qty - b.qty);
  } catch (e) {
    return [];
  }
}

function getBulkPrice(hit, qty) {
  if (!qty) return null;
  const tiers = getBulkTiers(hit);
  let matched = null;
  for (const tier of tiers) {
    if (tier.qty <= qty && (!matched || tier.qty > matched.qty)) {
      matched = tier;
    }
  }
  return matched ? matched.price : null;
}

// Mirrors snippets/get-best-price.liquid: takes the lowest of reseller/club/bulk
// price, never exceeding the product's actual selling price.
function computeBestPrice(hit, { bulkQty } = {}) {
  const actualPrice = hit.price;
  const clubBasePrice =
    hit.compare_at_price > actualPrice ? hit.compare_at_price : actualPrice;

  let price = actualPrice;

  const resellerPrice = getResellerPrice(hit);
  if (resellerPrice !== null && resellerPrice < price) price = resellerPrice;

  const clubPrice = getClubPrice(hit, clubBasePrice);
  if (clubPrice !== null && clubPrice < price) price = clubPrice;

  const bulkPrice = getBulkPrice(hit, bulkQty);
  if (bulkPrice !== null && bulkPrice < price) price = bulkPrice;

  if (price > actualPrice) price = actualPrice;

  return price;
}

function formatComputedPrice(amount) {
  // Round to the nearest cent explicitly: amount * 100 can land just below a
  // .5 boundary (e.g. 85.49999999999999) due to float imprecision, which
  // toFixed(2) then rounds down instead of to the nearest cent.
  return theme.Currency.formatMoney(
    Math.round(amount * 100),
    theme.settings.moneyFormat,
  );
}

// Mirrors snippets/product-bulk.liquid (reduce_on_mobile + show_only_first:
// true) as rendered in product-card.liquid for the grid card: only the
// lowest-quantity tier whose price beats the best price is shown, and
// nothing renders if no tier does.
function renderBulkTable(hit, html, bestPrice) {
  const matchedTier = getBulkTiers(hit).find((tier) => bestPrice > tier.price);
  if (!matchedTier) return "";
  const tiers = [matchedTier];

  return html`
    <div class="product-bulk">
      <ul class="product-bulk__list flex-col gap-2.5">
        ${tiers.map(
          (tier) => html`
            <li class="product-bulk__item">
              <span
                class="flex-1 w-1/2 text-center product-bulk__quantity hidden md:block"
              >
                ${algoliaShopify.translations.bulkQuantity.replace(
                  "[quantity]",
                  tier.qty,
                )}
              </span>
              <span
                class="flex-1 w-1/2 text-center product-bulk__quantity md:hidden"
              >
                ${tier.qty} =
              </span>
              <span class="flex-1 w-1/2 text-center product-bulk__price">
                <span class="product-bulk__price-amount"
                  >${formatComputedPrice(tier.price)}</span
                >
                <span class="product-bulk__price-unit"
                  >${algoliaShopify.translations.bulkUnit}</span
                >
              </span>
            </li>
          `,
        )}
      </ul>
    </div>
  `;
}

// Sélection de l'index Algolia et du format numérique (InstantSearch
// `numberLocale`) en fonction du marché Shopify et de la langue du visiteur,
// exposés en liquid dans theme.localization (snippets/js-variables.liquid).
// Clé du tableau : "<market>_<language>". À compléter avec les combinaisons
// réelles du store et les noms d'index créés dans le dashboard Algolia.
const ALGOLIA_INDEX_BY_MARKET_LOCALE = {
  "@default_fr": {
    indexName: "shopify_products",
    meaIndexName: "shopify_mea",
    numberLocale: "fr",
  },
  italie_it: {
    indexName: "shopify_products_italie_it",
    meaIndexName: "shopify_mea_it",
    numberLocale: "it",
  },
  "union-europeenne_en": {
    indexName: "shopify_products_union_europenne_en",
    meaIndexName: "shopify_mea_en",
    numberLocale: "en",
  },
  "pays-bas_nl": {
    indexName: "shopify_products_pays_bas_nl",
    meaIndexName: "shopify_mea_nl",
    numberLocale: "nl",
  },
  espagne_es: {
    indexName: "shopify_products_espagne_es",
    meaIndexName: "shopify_mea_es",
    numberLocale: "es",
  },
};

const ALGOLIA_DEFAULT_MARKET_LOCALE_CONFIG = {
  indexName: "shopify_products",
  meaIndexName: "shopify_mea",
  numberLocale: "fr",
};

function getAlgoliaMarketLocaleConfig() {
  const market = window.theme?.localization?.market || "@default";
  const language = window.theme?.localization?.language || "fr";
  const key = `${market}_${language}`;
  return (
    ALGOLIA_INDEX_BY_MARKET_LOCALE[key] || ALGOLIA_DEFAULT_MARKET_LOCALE_CONFIG
  );
}

// Date de MEA (metaobject collection_mea, champs date et heure) : timestamp
// en millisecondes généré en liquid (sections/main-collection.liquid), donc
// un instant absolu indépendant du fuseau du visiteur.
// false/vide => pas de borne.
function parseMeaDate(value) {
  if (typeof value !== "number" || !value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

// Une MEA est visible si :
// - startDate absente ou déjà atteinte,
// - endDate absente ou pas encore atteinte (masquée à partir de endDate),
// - markets vide ou contenant le handle du marché courant
//   (window.theme.localization.market, cf. snippets/js-variables.liquid).
function isMeaVisible(mea, now = new Date()) {
  const start = parseMeaDate(mea.startDate);
  if (start && now < start) return false;

  const end = parseMeaDate(mea.endDate);
  if (end && now >= end) return false;

  const markets = (
    Array.isArray(mea.markets) ? mea.markets : String(mea.markets || "").split(",")
  )
    .map((market) => market.trim().toLowerCase())
    .filter(Boolean);
  if (markets.length) {
    const currentMarket = (window.theme?.localization?.market || "").toLowerCase();
    if (!markets.includes(currentMarket)) return false;
  }

  return true;
}

document.addEventListener("algolia.hooks.initialize", function () {
  algoliaShopify.hooks.registerHook(
    "beforeRecommendHitTemplate",
    (_defaultTemplate, html, hit) => {
      ensureAlgoliaTranslations();
      return getInstantSearchProductCardTemplate(_defaultTemplate, hit, html);
    },
  );

  // ******************************** //
  // *******  INSTANTSEARCH  ******** //
  // ******************************** //

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchInitCollectionSortOrders",
    (...hookArgs) => {
      ensureAlgoliaTranslations();
      const sortOrders = hookArgs.slice(0, -1);

      return sortOrders.map(translateSortOrder);
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchInitSearchSortOrders",
    (...hookArgs) => {
      ensureAlgoliaTranslations();
      const sortOrders = hookArgs.slice(0, -1);

      return sortOrders.map(translateSortOrder);
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchConfigurationOptions",
    (options) => {
      ensureAlgoliaTranslations();
      const { indexName, numberLocale } = getAlgoliaMarketLocaleConfig();
      options.numberLocale = numberLocale;
      options.indexName = indexName;
      return options;
    },
  );

  // Insertion des cartes MEA (mise en avant éditoriale) dans la grille de
  // résultats. `position` est l'index global (1-based) dans la liste
  // complète et paginée des résultats : position 6 => 6ème élément de la
  // liste ; position 52 => 52ème élément, quelle que soit la page sur
  // laquelle il tombe. Si position dépasse le nombre réel de résultats,
  // la MEA n'apparaît nulle part.
  //
  // On s'appuie sur hit.__position, champ standard injecté par
  // algoliasearch-helper sur chaque hit ((page * hitsPerPage) + index + 1) :
  // c'est la position absolue 1-based du produit dans l'ensemble paginé des
  // résultats (confirmé par dump console — ne pas confondre avec hit.position,
  // qui est la position Shopify du produit dans la collection, statique).
  // Une première implémentation basée sur la page lue dans l'URL
  // (window.location.search) s'est avérée peu fiable : rien ne garantit que
  // l'URL est déjà à jour au moment où ce hook s'exécute (décalages observés
  // en navigation avant/arrière). __position est calculé par
  // algoliasearch-helper en même temps que les hits eux-mêmes, donc toujours
  // synchrone avec ce qui est réellement rendu.
  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchTransformItems",
    function (...hookArgs) {
      if (window.theme?.debugMea) {
        console.log("[MEA] beforeInstantSearchTransformItems args:", hookArgs);
      }

      // L'ordre exact des paramètres de ce hook n'est pas documenté
      // précisément par Algolia : on cherche le tableau d'items parmi les
      // arguments plutôt que de supposer sa position, pour ne jamais
      // renvoyer une valeur non-array (ça casse le .map() de l'app).
      const itemsIndex = hookArgs.findIndex((arg) => Array.isArray(arg));
      if (itemsIndex === -1) {
        return hookArgs[0];
      }
      const items = hookArgs[itemsIndex];

      // On repart des vrais produits à chaque appel pour ne pas dupliquer
      // les cartes MEA lors des re-render (tri, filtre, pagination).
      const pageProducts = items.filter((item) => !item.__isMea);

      const meaEntries = (window.theme?.collectionMea || []).filter((mea) =>
        isMeaVisible(mea),
      );
      if (!meaEntries.length || !pageProducts.length) {
        return pageProducts;
      }

      const firstPosition = pageProducts[0].__position;
      const lastPosition = pageProducts[pageProducts.length - 1].__position;
      if (window.theme?.debugMea) {
        console.log("[MEA] page range:", { firstPosition, lastPosition });
        console.log(
          "[MEA] premier hit complet (pour vérifier les champs disponibles):",
          pageProducts[0],
        );
      }
      if (!firstPosition || !lastPosition) {
        // Si l'app ne fournit pas cette info, on ne prend aucun risque
        // plutôt que d'afficher la MEA au mauvais endroit.
        return pageProducts;
      }

      // Les MEA proviennent de la collection et de ses parents (jusqu'à
      // tous-les-produits), sans priorité entre elles. Deux MEA sur la même
      // position s'affichent côte à côte : la seconde est décalée d'un cran
      // (6 -> 7), et ainsi de suite. Le tri est stable, l'ordre d'arrivée
      // (collection courante d'abord) départage les égalités.
      let previousPosition = 0;
      const placedMea = meaEntries
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((mea) => {
          const position = Math.max(mea.position, previousPosition + 1);
          previousPosition = position;
          return { mea, position };
        });

      const result = [...pageProducts];
      placedMea.forEach(({ mea, position }) => {
        if (position < firstPosition || position > lastPosition) {
          return;
        }

        const localIndex = position - firstPosition;
        // On retire un produit pour compenser l'ajout de la carte MEA
        // (elle compte dans la pagination, cf. besoin fonctionnel).
        if (result.length > 0) {
          result.pop();
        }
        result.splice(localIndex, 0, {
          objectID: `mea-${mea.id}`,
          __isMea: true,
          __meaData: mea,
        });
      });

      return result;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchMainTemplate",
    function (_defaultTemplate, data, html) {
      const subcollections =
        document.getElementById("main-subcollection-algolia")?.innerHTML || "";
      return html`
        <div class="collection with-sidebar ais-page">
          <facets-topbar
            id="FacetsTopbar-template--27716668162422__main-collection"
            class="collection__topbar xl:sticky z-10"
            data-gap="0"
          >
            <div class="page-width facet-topbar flex items-center gap-4">
              <div class="facet-wrapper">
                <div class="ais-input" style="display:none">
                  <div class="ais-search-box-container"></div>
                  <div class="ais-input-button">
                    <div class="ais-clear-input-icon"></div>
                  </div>
                </div>

                <button
                  class="button button--secondary icon-with-text"
                  type="button"
                  is="filters-toggle"
                  aria-controls="FacetFiltersContainer"
                  aria-expanded="true"
                >
                  <span
                    class="btn-fill"
                    data-fill=""
                    style="transform: translateY(var(--motion-translateY)); --motion-translateY: -76%;"
                  ></span>
                  <span class="btn-text"
                    ><svg
                      class="icon icon-filter icon-md"
                      viewBox="0 0 22 20"
                      stroke="currentColor"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        d="M1.5 14H4M4 14C4 15.6569 5.34315 17 7 17C8.65685 17 10 15.6569 10 14C10 12.3431 8.65685 11 7 11C5.34315 11 4 12.3431 4 14ZM18 6H20.5M18 6C18 7.65685 16.6569 9 15 9C13.3431 9 12 7.65685 12 6C12 4.34315 13.3431 3 15 3C16.6569 3 18 4.34315 18 6ZM13 14H20.5M1.5 6H9"
                      ></path></svg
                    >${algoliaShopify.translations.hideFilters}</span
                  >
                  <span class="btn-text btn-duplicate">
                    <svg
                      class="icon icon-filter icon-md"
                      viewBox="0 0 22 20"
                      stroke="currentColor"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        d="M1.5 14H4M4 14C4 15.6569 5.34315 17 7 17C8.65685 17 10 15.6569 10 14C10 12.3431 8.65685 11 7 11C5.34315 11 4 12.3431 4 14ZM18 6H20.5M18 6C18 7.65685 16.6569 9 15 9C13.3431 9 12 7.65685 12 6C12 4.34315 13.3431 3 15 3C16.6569 3 18 4.34315 18 6ZM13 14H20.5M1.5 6H9"
                      ></path>
                    </svg>
                    ${algoliaShopify.translations.showFiltersLabel}
                  </span>
                  <span class="btn-loader">
                    <span></span><span></span><span></span>
                  </span>
                </button>
              </div>

              <div class="ais-stats-container"></div>
              <div id="ais-subcollection-placeholder" class="grow"></div>

              <div class="ais-sort">
                ${data.multipleSortOrders
                  ? html`
                      ${algoliaShopify.translations.sortBy}
                      <span class="ais-sort-orders-container"></span>
                    `
                  : html` ${algoliaShopify.translations.sortByRelevance} `}
              </div>
            </div>
            <span
              class="collection__cutout cutout opacity-0 absolute left-0 top-100 w-full pointer-events-none"
            ></span>
          </facets-topbar>

          <div class="active-facets" id="FacetFiltersActive">
            <div class="ais-clear-refinements-container"></div>
            <div class="ais-current-refined-values-container"></div>
          </div>

          <div class="collection__content flex">
            <facet-sticky
              id="FacetFiltersContainer"
              class="facet-sticky z-25 fixed left-0 text-center w-full pointer-events-none block"
              data-scroll=""
              data-scroll-sticky=""
              data-scroll-target=".collection"
            >
              <button
                type="button"
                class="button button--primary icon-with-text pointer-events-auto"
                is="hover-button"
                aria-controls="FacetDrawerStickyAlgolia"
                aria-expanded="true"
                style="opacity: 1; visibility: visible; transform: translateY(0px);"
              >
                <span
                  class="btn-fill"
                  data-fill=""
                  style="transform: translateY(var(--motion-translateY)); --motion-translateY: -76%;"
                ></span>
                <span class="btn-text"
                  ><svg
                    class="icon icon-filter icon-sm"
                    viewBox="0 0 22 20"
                    stroke="currentColor"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      d="M1.5 14H4M4 14C4 15.6569 5.34315 17 7 17C8.65685 17 10 15.6569 10 14C10 12.3431 8.65685 11 7 11C5.34315 11 4 12.3431 4 14ZM18 6H20.5M18 6C18 7.65685 16.6569 9 15 9C13.3431 9 12 7.65685 12 6C12 4.34315 13.3431 3 15 3C16.6569 3 18 4.34315 18 6ZM13 14H20.5M1.5 6H9"
                    ></path></svg
                  >${algoliaShopify.translations.filterAndSort}</span
                >
                <span class="btn-loader">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>
              <drawer-element
                id="FacetDrawerStickyAlgolia"
                class="facet-drawer drawer drawer--start z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none"
                role="dialog"
                aria-modal="true"
                aria-label="${algoliaShopify.translations.filterAndSort}"
                data-section-id="template--26681208668534__main-collection"
              >
                <overlay-element
                  class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none"
                  aria-controls="FacetDrawerStickyAlgolia"
                  aria-expanded="true"
                ></overlay-element>
                <div
                  class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden"
                  style=""
                >
                  <gesture-element
                    class="drawer__header flex justify-between opacity-0 invisible relative"
                    tabindex="0"
                  >
                    <span
                      class="drawer__title heading lg:text-3xl text-2xl leading-none tracking-tight"
                      >${algoliaShopify.translations.filterAndSort}</span
                    >
                    <button
                      class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center"
                      type="button"
                      is="hover-button"
                      aria-controls="FacetDrawerStickyAlgolia"
                      aria-expanded="true"
                      aria-label="${theme.strings.close}"
                    >
                      <span
                        class="btn-fill"
                        data-fill=""
                        style="transform: translateY(var(--motion-translateY)); --motion-translateY: -76%;"
                      ></span>
                      <span class="btn-text"
                        ><svg
                          class="icon icon-close icon-sm"
                          viewBox="0 0 20 20"
                          stroke="currentColor"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M5 15L15 5M5 5L15 15"
                          ></path></svg
                        ><svg
                          class="icon icon-loader icon-sm absolute opacity-0 invisible animate-spin"
                          viewBox="0 0 18 18"
                          stroke="currentColor"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M0.7,9.9C0.5,5.2,4.1,1.2,8.5,1c4.7-0.3,8.9,3.8,8.8,8.8"
                          ></path></svg
                      ></span>
                      <span class="btn-loader">
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                    </button>
                  </gesture-element>
                  <div
                    class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink"
                    id="MobileFacetFiltersContainer"
                  >
                    <div
                      class="drawer__scrollable relative flex flex-col grow shrink"
                    >
                      <form
                        class="facet-form"
                        method="GET"
                        action=""
                        data-section-id="template--26681208668534__main-collection"
                        data-history=""
                      >
                        <div
                          class="details"
                          data-filter=""
                          data-index="sticky-page"
                          hidden=""
                        >
                          <input type="hidden" name="page" value="" />
                        </div>

                        <div class="ais-facets-button hidden">
                          ${algoliaShopify.translations.showFilters}
                        </div>

                        <div class="ais-facets">
                          <div class="ais-clear-refinements-container"></div>
                          <div
                            class="ais-current-refined-values-container"
                          ></div>
                          ${algoliaShopify.config
                            .app_blocks_dynamic_widgets_beta_enabled
                            ? html`
                                <div
                                  class="ais-dynamic-widgets-container"
                                ></div>
                              `
                            : data.facets.map((facet) => {
                                return html`
                                  <div
                                    class="ais-facet-dropdown-wrapper ais-facet-${facet.type} ais-facet-${facet.escapedName}"
                                  >
                                    <label
                                      for="${facet.escapedName}"
                                      class="ais-range-slider--header ais-facet--header ais-header"
                                    >
                                      ${algoliaShopify.translations.facetLabels[
                                        facet.name
                                      ] || facet.title}
                                      <svg
                                        class="icon icon-chevron-up icon-md"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          d="M6 15L12 9L18 15"
                                        ></path>
                                      </svg>
                                    </label>
                                    <input
                                      class="ais-dropdown-checkbox"
                                      type="checkbox"
                                      id="${facet.escapedName}"
                                      name="dropdown"
                                      checked
                                    />
                                    <div
                                      class="ais-facet-${facet.escapedName}-container ais-facet-dropdown-container"
                                    ></div>
                                  </div>
                                `;
                              })}
                        </div>
                      </form>
                      <div
                        class="drawer__sticky z-1 sticky left-0 bottom-0 w-full"
                      >
                        <button
                          class="button button--primary w-full facet-drawer__submit-button"
                          type="button"
                          is="hover-button"
                          aria-controls="FacetDrawerStickyAlgolia"
                          aria-expanded="true"
                        >
                          <span class="btn-fill" data-fill=""></span>
                          <span class="btn-text flex gap-1"
                            >${algoliaShopify.translations.viewResults}
                          </span>
                          <span class="btn-loader">
                            <span></span>
                            <span></span>
                            <span></span>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </drawer-element>
            </facet-sticky>
            <div class="ais-block" id="ProductGridContainer">
              <div class="ais-search-header">
                <div class="ais-change-display">
                  <span
                    class="ais-change-display-block ais-change-display-selected"
                    ><i class="fa fa-th-large"></i
                  ></span>
                  <span class="ais-change-display-list"
                    ><i class="fa fa-th-list"></i
                  ></span>
                </div>
              </div>
              <div class="ais-hits-container ais-results-as-block"></div>
            </div>
          </div>
          <div class="ais-pagination-container"></div>
        </div>
      `;
    },
  );

  setTimeout(() => {
    const target = document.getElementById("ais-subcollection-placeholder");
    const source = document.getElementById("main-subcollection-algolia");
    if (target && source) target.innerHTML = source.innerHTML;

    // Centre la sous-collection active dans la liste défilante
    const list = target?.querySelector(".subcollections");
    const active = list?.querySelector(".active");
    if (active) {
      list.scrollLeft =
        active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2;
    }
  }, 0);

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchStatsTemplate",
    function (_defaultTemplate, data, html) {
      if (!data.query) {
        if (!data.nbHits) {
          return html` ${algoliaShopify.translations.noResults} `;
        }

        // data.page est l'index de page Algolia, 0-based (page 1 => data.page === 0).
        const productsStartingIndex = (data.page || 0) * data.hitsPerPage + 1;
        const productsEndingIndex = Math.min(
          productsStartingIndex + data.hitsPerPage - 1,
          data.nbHits,
        );

        return html`(
        ${algoliaShopify.translations.productsCount
          .replace(
            "[from]",
            algoliaShopify.helpers.formatNumber(productsStartingIndex),
          )
          .replace(
            "[to]",
            algoliaShopify.helpers.formatNumber(productsEndingIndex),
          )
          .replace("[total]", algoliaShopify.helpers.formatNumber(data.nbHits))}
        )`;
      }
      return html`
        ${data.hasOneResult &&
        html`
          ${algoliaShopify.translations.searchResultsLabel}
          <strong>${data.query}</strong> - 1
          ${algoliaShopify.translations.resultSingular}
          </strong>
        `}
        ${data.hasManyResults &&
        html`
          ${algoliaShopify.translations.searchResultsLabel}
          <strong>${data.query}</strong> ${" - "}
          ${algoliaShopify.helpers.formatNumber(data.nbHits)}
          ${algoliaShopify.translations.resultsPlural}
        `}
      `;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchProductTemplate",
    function (_defaultTemplate, hit, html, components, trackSearchAttribution) {
      if (hit.__isMea) {
        return getMeaCardTemplate(hit.__meaData, html);
      }
      return getInstantSearchProductCardTemplate(
        _defaultTemplate,
        hit,
        html,
        components,
        trackSearchAttribution,
      );
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeInstantSearchFacetParamsOptions",
    (params, facet) => {
      if (facet.type == "conjunctive" || facet.type == "disjunctive") {
        return {
          ...params,
          templates: {
            item(item, { html }) {
              ensureAlgoliaTranslations();
              let checked = item.isRefined == true ? "checked" : "";
              const label = translateFacetValueLabel(item.label);
              return html` <div class="ais-RefinementList-label">
                <label
                  for="sidebar-${handleize(item.label)}"
                  class="checkbox-label flex items-baseline gap-1"
                >
                  <input
                    id="sidebar-${handleize(item.label)}"
                    class="checkbox"
                    type="checkbox"
                    checked="${checked}"
                  />
                  ${label}<span class="text-sm text-opacity"
                    >(${item.count.toLocaleString()})</span
                  >
                </label>
              </div>`;
            },
          },
        };
      }
      if (facet.type == "slider") {
        return {
          ...params,
          tooltips: {
            format: (value) =>
              `${algoliaShopify.helpers.formatNumber(Math.round(value))} €`,
          },
        };
      }
      return {
        ...params,
      };
    },
  );

  // ******************************** //
  // *******  AUTOCOMPLETE  ********* //
  // ******************************** //

  algoliaShopify.hooks.registerHook(
    "beforeAutocompletePluginsArray",
    function () {
      ensureAlgoliaTranslations();
      var plugins = Array.prototype.slice.call(arguments);

      const { createLocalStorageRecentSearchesPlugin } =
        window["@algolia/autocomplete-plugin-recent-searches"];
      const { createQuerySuggestionsPlugin } =
        window["@algolia/autocomplete-plugin-query-suggestions"];
      const { searchClient } = window.algoliaShopify;

      const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
        translations: {
          removeSearchTitle: algoliaShopify.translations.removeSearchTitle,
          fillQueryTitle(text) {
            return `${algoliaShopify.translations.searchFor} "${text}"`;
          },
        },
        transformSource({ source }) {
          return {
            ...source,
            getItemUrl({ item }) {
              return `/search?q=${item.query}`;
            },
            templates: {
              header(params) {
                const { html } = params;
                return html`
                  <div class="aa-SourceHeader">
                    <span class="aa-SourceHeaderTitle"
                      >${algoliaShopify.translations.trendingSearches}</span
                    >
                    <div class="aa-SourceHeaderLine" />
                  </div>
                `;
              },
              item(params) {
                const { item, html } = params;

                return html`<a
                  class="aa-ItemLink"
                  href="/search?q=${item.label}"
                >
                  ${source.templates.item(params).props.children}
                </a>`;
              },
            },
          };
        },
      });

      plugins.push(recentSearchesPlugin);

      if (window.algoliaShopify.config.autocomplete_query_suggestions) {
        const querySuggestionsPlugin = createQuerySuggestionsPlugin({
          searchClient,
          indexName: window.algoliaShopify.config.query_suggestions_index_name,
          getSearchParams() {
            return recentSearchesPlugin.data.getAlgoliaSearchParams({
              hitsPerPage:
                window.algoliaShopify.config
                  .suggestions_autocomplete_hits_per_page,
            });
          },
          transformSource({ source }) {
            return {
              ...source,
              getItemUrl({ item }) {
                return `/search?q=${item.query}`;
              },
              templates: {
                item(params) {
                  const { item, html } = params;

                  return html`<a
                    class="aa-ItemLink"
                    href="/search?q=${item.query}"
                  >
                    ${source.templates.item(params).props.children}
                  </a>`;
                },
              },
            };
          },
        });

        const querySuggestionsPluginIndex = plugins.findIndex(
          (plugin) => plugin.name === "aa.querySuggestionsPlugin",
        );

        plugins[querySuggestionsPluginIndex] = querySuggestionsPlugin;
      }

      return plugins;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteOptions",
    function (options) {
      ensureAlgoliaTranslations();
      const { searchClient } = algoliaShopify;
      const { getAlgoliaFacets } = window["@algolia/autocomplete-js"];
      const { getAlgoliaResults } = window.algoliaShopify.externals;

      options.placeholder = algoliaShopify.translations.searchPlaceholder;
      options.openOnFocus = true;

      const brandPlugin = {
        name: "brandPlugin",
        getSources({ query }) {
          return [
            {
              sourceId: "brands",
              getItems({ query }) {
                return getAlgoliaFacets({
                  searchClient,
                  queries: [
                    {
                      indexName: getAlgoliaMarketLocaleConfig().indexName,
                      facet: "vendor",
                      params: {
                        facetQuery: query,
                        maxFacetHits: 10,
                      },
                    },
                  ],
                });
              },
              templates: {
                header() {
                  return algoliaShopify.translations.topBrands;
                },
                item({ item, html }) {
                  return html`<a href="/collections/vendors?q=${item.label}"
                    >${item.label}</a
                  >`;
                },
              },
            },
          ];
        },
      };
      options.plugins.push(brandPlugin);

      const meaPlugin = {
        name: "meaPlugin",
        getSources({ query }) {
          return [
            {
              sourceId: "mea",
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: getAlgoliaMarketLocaleConfig().meaIndexName,
                      params: {
                        hitsPerPage: 1,
                      },
                    },
                  ],
                });
              },
              templates: {
                header() {
                  return algoliaShopify.translations.rightNow;
                },
                item({ item, html }) {
                  return html`
                    <div class="aa-Mea">
                      <div class="aa-MeaPicture">
                        <img
                          src="${item.image}"
                          sizes="(max-width: 500px) 100vw, 220px"
                          alt=""
                        />
                      </div>
                      <div>
                        <span class="aa-MeaTitle">${item.titre}</span>
                        <a
                          href="${item.url}"
                          class="button button--secondary button--fixed aa-MeaLink button--sm"
                          is="hover-link"
                        >
                          <span class="btn-fill" data-fill></span>
                          <span class="btn-text"
                            >${algoliaShopify.translations.discoverOffer}</span
                          >
                        </a>
                      </div>
                    </div>
                  `;
                },
              },
            },
          ];
        },
      };
      options.plugins.push(meaPlugin);

      const articlePagesPlugin = {
        name: "articlePagesPlugin",
        getSources({ query }) {
          return [
            {
              sourceId: "articlesPages",
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: "shopify_articles_pages",
                      params: {
                        query,
                        hitsPerPage: 3,
                      },
                    },
                  ],
                  transformResponse({ hits }) {
                    const addUrls = (hitsArray) => {
                      hitsArray.forEach((hit) => {
                        if (hit.blog) {
                          hit.url = `/blogs/${handleize(hit.blog.title)}/${hit.handle}`;
                        } else {
                          hit.url = `/pages/${hit.handle}`;
                        }
                      });
                    };

                    if (Array.isArray(hits[0])) {
                      hits.forEach(addUrls);
                    } else {
                      addUrls(hits);
                    }
                    return hits;
                  },
                });
              },
              templates: {
                header() {
                  return algoliaShopify.translations.quickAccess;
                },
                item({ item, html }) {
                  return html`
                    <div class="aa-ItemContentTitle">
                      <a href="${item.url}">${item.title}</a>
                    </div>
                  `;
                },
                noResults({ html }) {
                  const pages = window.algoliaDefaultPages || [];

                  return html`
                    <ul
                      class="aa-List"
                      role="listbox"
                      aria-labelledby="autocomplete-0-label"
                      id="autocomplete-0-articlesPages-list"
                    >
                      ${pages.map(
                        (page, index) => html`
                          <li
                            class="aa-Item"
                            id="autocomplete-0-articlesPages-item-${31 + index}"
                            role="option"
                            aria-selected="${index === 0 ? "true" : "false"}"
                          >
                            <div class="aa-ItemContentTitle">
                              <a href="${page.url}">${page.title}</a>
                            </div>
                          </li>
                        `,
                      )}
                    </ul>
                  `;
                },
              },
            },
          ];
        },
      };
      options.plugins.push(articlePagesPlugin);

      const bestSellerPlugin = {
        name: "bestseller_products",
        getSources({ query }) {
          return [
            {
              sourceId: "bestseller_products",
              getItems() {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: getAlgoliaMarketLocaleConfig().indexName,
                      params: {
                        hitsPerPage: 6,
                      },
                    },
                  ],
                });
              },
              templates: {
                header({ html }) {
                  return html`
                    <div class="aa-SourceHeader">
                      <span class="aa-SourceHeaderTitle">
                        ${algoliaShopify.translations.bestSellers}
                      </span>
                      <div class="aa-SourceHeaderLine" />
                    </div>
                  `;
                },
                item({ item, components, html, itemLink, distinct }) {
                  return getAutocompleteProductTemplate(
                    item,
                    components,
                    html,
                    itemLink,
                    distinct,
                  );
                },
                noResults() {
                  return algoliaShopify.translations.noResults;
                },
              },
            },
          ];
        },
      };

      options.plugins.push(bestSellerPlugin);

      return options;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteMainTemplate",
    function (_defaultTemplate, templateOptions, elements, displaySuggestions) {
      const { html, state } = templateOptions;
      return html`
        <div class="aa-PanelLayout aa-Panel--scrollable">
          <div class="aa-PanelSections">
            <div class="aa-PanelSectionMea">${elements.mea}</div>
            <div class="aa-PanelSectionSuggestion">
              ${!elements.products &&
              html`
                <div class="aa-SourceHeader">
                  <span class="aa-SourceHeaderTitle"
                    >${algoliaShopify.translations.trendingSearches}</span
                  >
                  <div class="aa-SourceHeaderLine" />
                </div>
                ${algoliaShopify.translations.noResultsFor}
                "${templateOptions.state.query}"
              `}
              ${elements.recentSearchesPlugin}
              ${displaySuggestions &&
              html` ${elements.querySuggestionsPlugin} `}
              ${elements.redirectUrlPlugin} ${elements.brands}
            </div>
            <div class="aa-PanelSectionProducts">
              ${elements.products ?? elements.bestseller_products}
              ${elements.articlesPages}
            </div>
          </div>
        </div>
      `;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteHeaderTemplate",
    (_defaultTemplate, templateOptions, resource) => {
      const { html, state } = templateOptions;

      return html`
        <div class="aa-SourceHeader">
          <span class="aa-SourceHeaderTitle">
            ${state.query
              ? html`${algoliaShopify.translations.bestSellersFor}
                ${state.query}`
              : html`${algoliaShopify.translations.bestSellers}`}
          </span>
          <div class="aa-SourceHeaderLine" />
        </div>
      `;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteProductTemplate",
    function (
      _defaultTemplate,
      templateOptions,
      distinct,
      itemLink,
      trackSearchAttribution,
    ) {
      // Modify default options, then return them
      const { html, item, components } = templateOptions;

      return getAutocompleteProductTemplate(
        item,
        components,
        html,
        itemLink,
        distinct,
      );
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteArticlesTemplate",
    function (_defaultTemplate, templateOptions, itemLink) {
      const { item, html, components } = templateOptions;
      return html`
        <a href="${itemLink}" class="aa-ItemLink">
          <div class="aa-ItemWrapper">
            <div class="aa-ItemContent">
              <div class="aa-ItemContentBody">
                <div class="aa-ItemContentTitle">
                  ${components.Highlight({ hit: item, attribute: "title" })}
                </div>
              </div>
            </div>
          </div>
        </a>
      `;
    },
  );

  algoliaShopify.hooks.registerHook(
    "beforeAutocompletePagesTemplate",
    function (_defaultTemplate, templateOptions, itemLink) {
      const { html, item, components } = templateOptions;
      return html`
        <a href="${itemLink}" class="aa-ItemLink aa-ProductItem">
          <div class="aa-ItemWrapper">
            <div class="aa-ItemContent">
              <div class="aa-ItemContentBody">
                <div class="aa-ItemContentTitle">
                  ${components.Highlight({ hit: item, attribute: "title" })}
                </div>
              </div>
            </div>
          </div>
        </a>
      `;
    },
  );

  function getAutocompleteProductTemplate(
    item,
    components,
    html,
    itemLink,
    distinct,
  ) {
    const bestPrice = computeBestPrice(item);
    const onSale = item.compare_at_price > bestPrice;
    const savedAmount = onSale
      ? Math.round(
          ((item.compare_at_price - bestPrice) / item.compare_at_price) * 100,
        )
      : 0;
    const show_badge = onSale && savedAmount >= 20;

    let badge = null;
    let badgeClass = null;
    let showBadges = false;

    const today = Math.floor(Date.now() / 1000);
    const endNewProductRaw = item.meta?.custom?.end_new_product;
    const endNewProduct = endNewProductRaw
      ? Math.floor(new Date(endNewProductRaw).getTime() / 1000)
      : null;
    if (endNewProduct && today < endNewProduct && savedAmount < 20) {
      badge = algoliaShopify.translations.newBadge;
      badgeClass = "nouveau";
      showBadges = true;
    }

    const pastille = item.meta?.akeneo?.pastille;
    if (pastille === "Soldes" || pastille === "Blackweek") {
      badge = pastille;
      badgeClass = handleize(pastille);
      showBadges = true;
    }

    if (!item.inventory_available) {
      if (isRestocking(item)) {
        badge = algoliaShopify.translations.restocking;
        badgeClass = "restocking";
      } else {
        badge = algoliaShopify.translations.outOfStock;
        badgeClass = "out-of-stock";
      }
      showBadges = true;
    }

    return html`
      <a
        href="${itemLink ? itemLink : productUrl(item.handle)}"
        class="aa-ItemLink aa-ProductItem ${badgeClass}"
        onClick="${() => trackSearchAttribution(item)}"
      >
        <div class="aa-ItemContent">
          <div class="aa-ItemPicture aa-ItemPicture--loaded">
            ${(() => {
              const img = algoliaShopify.helpers.renderImage(item, 300);
              return html`
                <img
                  src="${img.src}"
                  srcset="${img.srcset}"
                  sizes="${img.sizes}"
                  alt="${item.title}"
                />
              `;
            })()}
          </div>
          <div class="aa-ItemContentBody">
            ${showBadges &&
            html`
              <span class="product-badge product-badge__${badgeClass}"
                >${badge}</span
              >
            `}
            <div class="aa-ItemContentBrand">
              ${item.product_type &&
              components.Highlight({
                hit: item,
                attribute: "product_type",
              })}
              removing vendor
            </div>
            <div class="aa-ItemContentTitleWrapper">
              <div class="aa-ItemContentTitle">
                ${components.Highlight({
                  hit: item,
                  attribute: "title",
                })}
              </div>
            </div>
            <div class="aa-ItemContentPrice">
              <div class="aa-ItemContentPriceCurrent">
                ${formatComputedPrice(bestPrice)}
              </div>
              ${show_badge &&
              html`
                <div class="product__badges" id="Badges--${item.id}">
                  <div class="badges flex flex-wrap gap-3">
                    <span
                      class="badge badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full"
                      >-${savedAmount}%</span
                    >
                  </div>
                </div>
              `}
              ${onSale &&
              html`
                <span
                  class="product__price--stroke inline-flex items-center h-auto relative"
                >
                  ${formatComputedPrice(item.compare_at_price)}
                </span>
              `}
            </div>
          </div>
        </div>
      </a>
    `;
  }

  // Carte de mise en avant éditoriale/promotionnelle (MEA), alimentée par
  // collection.metafields.custom.mea (metaobject collection_mea).
  // Même format qu'une carte produit (classe product-card--mea, pas de
  // grid-column/row-span) : voir assets/algolia.css.
  function getMeaCardTemplate(mea, html) {
    if (!mea) return html``;

    // Tous les champs du metaobject sont facultatifs : sans URL la carte
    // n'est pas cliquable, sans libellé le CTA n'est pas affiché.
    const content = html`
      ${mea.image &&
      html`
        <img
          class="product-card--mea__image"
          src="${mea.image}"
          alt="${mea.title || ""}"
          width="1200"
          height="1200"
          loading="lazy"
        />
      `}
      <div class="product-card--mea__overlay"></div>
      <div class="product-card--mea__content">
        ${mea.brandImage &&
        html`
          <img
            class="product-card--mea__brand"
            src="${mea.brandImage}"
            alt=""
            loading="lazy"
          />
        `}
        ${mea.title &&
        html`<p class="product-card--mea__title">${mea.title}</p>`}
        ${mea.ctaLabel &&
        html`<span class="product-card--mea__cta">${mea.ctaLabel}</span>`}
      </div>
    `;

    return html`
      <div
        class="ais-hit card product-card product-card--mea"
        style="--mea-gradient-color: ${mea.gradientColor || "#000000"};"
        data-mea-id="${mea.id}"
      >
        ${mea.ctaUrl
          ? html`
              <a
                class="product-card__media product-card--mea__link"
                href="${mea.ctaUrl}"
                aria-label="${mea.title || ""}"
              >
                ${content}
              </a>
            `
          : html`
              <div class="product-card__media product-card--mea__link">
                ${content}
              </div>
            `}
      </div>
    `;
  }

  function getInstantSearchProductCardTemplate(
    _defaultTemplate,
    hit,
    html,
    components,
    trackSearchAttribution,
  ) {
    let badge = null;
    let badgeClass = null;
    let showBadges = false;

    const bestPrice = computeBestPrice(hit);
    const onSale = hit.compare_at_price > bestPrice;
    const savedAmount = onSale
      ? Math.round(
          ((hit.compare_at_price - bestPrice) / hit.compare_at_price) * 100,
        )
      : 0;
    const today = Math.floor(Date.now() / 1000);
    const endNewProductRaw = hit.meta?.custom?.end_new_product;
    const endNewProduct = endNewProductRaw
      ? Math.floor(new Date(endNewProductRaw).getTime() / 1000)
      : null;
    if (endNewProduct && today < endNewProduct && savedAmount < 20) {
      badge = algoliaShopify.translations.newBadge;
      badgeClass = "nouveau";
      showBadges = true;
    }

    const pastille = hit.meta?.akeneo?.pastille;
    if (pastille === "Soldes" || pastille === "Blackweek") {
      badge = pastille;
      badgeClass = handleize(pastille);
      showBadges = true;
    }

    if (!hit.inventory_available) {
      if (isRestocking(hit)) {
        badge = algoliaShopify.translations.restocking;
        badgeClass = "restocking";
      } else {
        badge = algoliaShopify.translations.outOfStock;
        badgeClass = "out-of-stock";
      }
      showBadges = true;
    }

    const show_badge = onSale && savedAmount >= 20;

    // Quick add bypasses the dedicated add-to-cart flow on the racket test
    // product page (conditions popup, _test_racket line item property, and the
    // companion product it relies on), so it's disabled here for those products.
    const natureRaw = hit.meta?.akeneo?.nature;
    const nature = Array.isArray(natureRaw)
      ? natureRaw.join(",")
      : String(natureRaw || "");
    const isTestRacket = nature.toLowerCase().includes("test");

    const quickAddLabel =
      hit.template_suffix === "pre-order"
        ? theme.variantStrings.preOrder
        : algoliaShopify.translations.addToCart;

    // "Ajouter au panier" direct uniquement pour les produits à une seule variante.
    // Sinon, ProductCardSizeVariants récupère les variantes en stock via le
    // template quick-add-variants (un hit ne connait pas le stock par variante).
    const hasSingleVariant = hit.variants_count === 1;

    const cartIcon = html`
      <svg
        class="icon icon-cart icon-sm md:hidden"
        viewBox="0 0 24 24"
        stroke="currentColor"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M1 1h.5v0c.226 0 .339 0 .44.007a3 3 0 0 1 2.62 1.976c.034.095.065.204.127.42l.17.597m0 0 1.817 6.358c.475 1.664.713 2.496 1.198 3.114a4 4 0 0 0 1.633 1.231c.727.297 1.592.297 3.322.297h2.285c1.75 0 2.626 0 3.359-.302a4 4 0 0 0 1.64-1.253c.484-.627.715-1.472 1.175-3.161l.06-.221c.563-2.061.844-3.092.605-3.906a3 3 0 0 0-1.308-1.713C19.92 4 18.853 4 16.716 4H4.857ZM12 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"
        ></path>
      </svg>
    `;

    let variants = false;
    if (hit.meta?.custom?.product_variants_json_algolia) {
      variantsObj = JSON.parse(hit.meta.custom.product_variants_json_algolia);
      variants = Object.values(variantsObj.variantes);
    }

    function cleanValue(value) {
      if (typeof value === "string") {
        return value.replace(/\[\"|\"\]/g, "");
      } else if (Array.isArray(value)) {
        return value.join(", ");
      }
      return value;
    }

    function getImageUrlFromGid(gid) {
      if (!gid) return null;
      return window.specsIconsMapping[gid];
    }

    function getIconVariant(value) {
      if (!value) return null;
      return window.specsIconsVariantsMapping[value];
    }

    const product_specs =
      window.product_specs?.sort(
        (a, b) => (a.position || 0) - (b.position || 0),
      ) || [];

    let hasSpecs = false;
    product_specs.map((entry) => {
      const [keyFirst, keyLast] = (entry.key || "")
        .split(".")
        .map((s) => s.trim());
      let value = null;

      if (keyFirst && keyLast) {
        value = hit.meta?.[keyFirst]?.[keyLast];
      }

      if (value !== undefined && value !== null && value !== "") {
        hasSpecs = true;
      }
    });

    return html`
      <div
        class="ais-hit ais-product card product-card product-card--standard flex flex-col leading-none relative"
        style="--motion-translateY: 0px; opacity: 1; visibility: visible;"
        data-algolia-index="${hit.index}"
        data-algolia-position="${hit.productPosition}"
        data-algolia-queryid="${hit.queryID}"
        data-algolia-objectid="${hit.objectID}"
        data-handle="${hit.handle}"
        data-variant-id="${hit.objectID}"
        data-distinct="${hit._distinct}"
        data-product-id="${hit.id}"
        onClick="${() => trackSearchAttribution(hit)}"
      >
        <div class="product-card__media relative h-auto">
          ${isTestRacket
            ? html`
                <a
                  href="${productUrl(hit.handle)}"
                  class="quick-view__button button button--secondary z-2 absolute top-0 right-0 opacity-0"
                  is="hover-link"
                >
                  <span class="btn-fill" data-fill=""></span>
                  <span class="btn-text">
                    <svg
                      class="icon icon-eye icon-sm"
                      viewBox="0 0 20 20"
                      stroke="currentColor"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        d="M18.3334 10C18.3334 12.0833 15.8334 16.6667 10 16.6667C4.16669 16.6667 1.66669 12.0833 1.66669 10C1.66669 7.91668 4.16669 3.33334 10 3.33334C15.8334 3.33334 18.3334 7.91668 18.3334 10Z"
                      ></path>
                      <path
                        stroke-linecap="round"
                        d="M12.5 10C12.5 11.3807 11.3807 12.5 10 12.5C8.61931 12.5 7.50002 11.3807 7.50002 10C7.50002 8.6193 8.61931 7.50001 10 7.50001C11.3807 7.50001 12.5 8.6193 12.5 10Z"
                      ></path>
                    </svg>
                    <span class="sr-only"
                      >${algoliaShopify.translations.quickView}</span
                    >
                  </span>
                </a>
              `
            : html`
                <button
                  type="button"
                  class="quick-view__button button button--secondary z-2 absolute top-0 right-0 opacity-0"
                  is="hover-button"
                  aria-controls="Quickview-template__main-collection-${hit.handle}-${hit.objectID}"
                  aria-expanded="false"
                >
                  <span class="btn-fill" data-fill=""></span>
                  <span class="btn-text">
                    <svg
                      class="icon icon-eye icon-sm"
                      viewBox="0 0 20 20"
                      stroke="currentColor"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        stroke-linecap="round"
                        d="M18.3334 10C18.3334 12.0833 15.8334 16.6667 10 16.6667C4.16669 16.6667 1.66669 12.0833 1.66669 10C1.66669 7.91668 4.16669 3.33334 10 3.33334C15.8334 3.33334 18.3334 7.91668 18.3334 10Z"
                      ></path>
                      <path
                        stroke-linecap="round"
                        d="M12.5 10C12.5 11.3807 11.3807 12.5 10 12.5C8.61931 12.5 7.50002 11.3807 7.50002 10C7.50002 8.6193 8.61931 7.50001 10 7.50001C11.3807 7.50001 12.5 8.6193 12.5 10Z"
                      ></path>
                    </svg>
                    <span class="sr-only"
                      >${algoliaShopify.translations.quickView}</span
                    >
                  </span>
                  <span class="btn-loader"
                    ><span></span><span></span><span></span
                  ></span>
                </button>

                <quick-view
                  id="Quickview-template__main-collection-${hit.handle}-${hit.objectID}"
                  class="quick-view x-modal drawer z-40 fixed bottom-0 left-0 h-full w-full pointer-events-none"
                  data-product-url="${productUrl(hit.handle)}"
                  role="dialog"
                  aria-label="${algoliaShopify.translations
                    .chooseOptionsFor} ${hit.title}"
                  aria-modal="true"
                  hidden=""
                >
                  <overlay-element
                    class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none"
                    aria-controls="Quickview-template__main-collection-${hit.handle}-${hit.objectID}"
                    aria-expanded="false"
                  ></overlay-element>
                  <div
                    class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden"
                  >
                    <gesture-element
                      class="drawer__header flex justify-between opacity-0 invisible relative"
                      tabindex="0"
                    >
                      <button
                        class="button button--secondary button--close drawer__close z-1 absolute top-0 right-0 flex items-center justify-center opacity-0"
                        type="button"
                        is="hover-button"
                        aria-controls="Quickview-template__main-collection-${hit.handle}-${hit.objectID}"
                        aria-expanded="false"
                        aria-label="${theme.strings.close}"
                      >
                        <span class="btn-fill" data-fill=""></span>
                        <span class="btn-text"
                          ><svg
                            class="icon icon-close icon-sm"
                            viewBox="0 0 20 20"
                            stroke="currentColor"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M5 15L15 5M5 5L15 15"
                            ></path></svg
                        ></span>
                      </button>
                    </gesture-element>
                    <div
                      class="quick-view__content drawer__content opacity-0 invisible flex flex-col h-full grow shrink"
                    ></div>
                  </div>
                </quick-view>
              `}

          ${showBadges &&
          html`
            <span class="product-badge product-badge__${badgeClass}"
              >${badge}</span
            >
          `}

          <a
            class="block relative media media--square overflow-hidden"
            href="${productUrl(hit.handle)}"
            aria-label="${hit.title}"
            tabindex="-1"
          >
            <img
              src="${algoliaShopify.helpers.grandeImage(hit)}"
              alt="${hit.title} - ${hit.variant_title}"
              width="1200"
              height="1200"
              loading="eager"
            />
          </a>
          <div
            class="quick-add flex justify-end md:justify-center absolute w-full z-1 pointer-events-none"
          >
            ${hit.inventory_available &&
            !isTestRacket &&
            hasSingleVariant &&
            html`
              <form
                method="post"
                action="/cart/add"
                id="QuickAddProductForm-template--main-collection-${hit.handle}-${hit.objectID}"
                accept-charset="UTF-8"
                class="shopify-product-form"
                enctype="multipart/form-data"
                is="product-form"
              >
                <input type="hidden" name="form_type" value="product" />
                <input type="hidden" name="utf8" value="✓" />
                <input type="hidden" name="id" value="${hit.objectID}" />
                <button
                  type="submit"
                  class="button button--primary pointer-events-auto md:opacity-0"
                  is="hover-button"
                  data-variant-id="${hit.objectID}"
                  aria-label="${quickAddLabel}"
                >
                  <span class="btn-fill" data-fill=""></span>
                  <span class="btn-text"
                    >${cartIcon}
                    <span
                      class="hidden md:block"
                      data-sold-out="${algoliaShopify.translations.outOfStock}"
                      >${quickAddLabel}</span
                    >
                  </span>
                  <span class="btn-loader">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </button>
                <input
                  type="hidden"
                  name="product-id"
                  value="${hit.id}"
                /><input
                  type="hidden"
                  name="section-id"
                  value="template--27516966830420__main-collection"
                />
              </form>
            `}
            ${hit.inventory_available &&
            !isTestRacket &&
            !hasSingleVariant &&
            html`
              <div
                class="buttons hidden pointer-events-auto md:flex md:justify-center md:opacity-0"
              >
                <fieldset
                  is="product-card-size-variants"
                  class="js product-form__input variant-input-wrapper relative"
                  data-product-handle="${hit.handle}"
                  data-product-title="${hit.title}"
                  data-form-id="QuickAddProductForm-template--main-collection-${hit.handle}-${hit.objectID}"
                  data-add-to-cart-label="${quickAddLabel}"
                  data-sold-out-label="${algoliaShopify.translations.outOfStock}"
                ></fieldset>
              </div>
              <button
                type="button"
                class="button button--primary pointer-events-auto md:opacity-0"
                is="hover-button"
                aria-controls="Quickview-template__main-collection-${hit.handle}-${hit.objectID}"
                aria-expanded="false"
                aria-label="${algoliaShopify.translations.chooseOptions}"
                data-choose-options
              >
                <span class="btn-fill" data-fill=""></span>
                <span class="btn-text"
                  >${cartIcon}
                  <span class="hidden md:block"
                    >${algoliaShopify.translations.chooseOptions}</span
                  >
                </span>
              </button>
            `}
          </div>
        </div>

        <div
          class="product-card__content grow flex flex-col justify-start text-left w-full"
        >
          <div class="product-card__top w-full">
            <span class="sr-only"
              >${algoliaShopify.translations.vendorLabel}</span
            >
            <a
              class="caption reversed-link uppercase leading-none"
              href="/collections/vendors?q=${hit.vendor}"
              title="${hit.vendor}"
            >
              ${hit.vendor &&
              components &&
              components.Highlight({ attribute: "vendor", hit })}
            </a>
          </div>
          <div
            class="yotpo-widget-instance"
            data-yotpo-instance-id="1307934"
            data-yotpo-product-id="${hit.id}"
            data-yotpo-cart-product-id=""
            data-yotpo-section-id="collection"
          ></div>
          <div
            class="product-card__details flex flex-col items-baseline gap-2 w-full"
          >
            <p class="grow">
              <a
                class="product-card__title reversed-link text-base-xl font-medium leading-tight"
                href="${algoliaShopify.helpers.instantsearchLink(hit)}"
                title="${algoliaShopify.helpers.fullTitle(
                  hit.title,
                  hit._distinct,
                  hit.variant_title,
                )}"
              >
                ${components
                  ? html` ${components.Highlight({ attribute: "title", hit })} `
                  : hit.title}
              </a>
            </p>
            <div class="flex items-center gap-1">
              <div class="flex flex-col gap-2">
                <div
                  class="price price--on-sale flex flex-wrap gap-1 items-center"
                >
                  <span class="price__regular whitespace-nowrap"
                    >${formatComputedPrice(bestPrice)}</span
                  >

                  ${show_badge &&
                  html`
                    <div class="product__badges" id="Badges--${hit.id}">
                      <div class="badges flex flex-wrap gap-3">
                        <span
                          class="badge badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full"
                          >-${savedAmount}%</span
                        >
                      </div>
                    </div>
                  `}
                  ${onSale &&
                  html`
                    <span
                      class="product__price--stroke inline-flex items-center h-auto relative"
                    >
                      ${formatComputedPrice(hit.compare_at_price)}
                    </span>
                  `}
                </div>
              </div>

              ${(() => {
                const triggers = [
                  "Exclusion permanente",
                  "Exclusion temporaire",
                  "Exclusion marge",
                ];

                if (!pastille || !triggers.includes(pastille)) return "";

                return html`
                  <div
                    class="product__red-dot flex gap-1d5 items-center justify-start"
                  >
                    <span class="product__red-dot--circle"></span>
                  </div>
                `;
              })()}
            </div>

            ${renderBulkTable(hit, html, bestPrice)}
          </div>
          <div class="product-card__bottom flex items-center gap-2">
            ${variants &&
            html`
              <ul
                class="swatches swatches--round swatches--variant swatches--15487432589686 inline-flex items-start gap-2"
              >
                ${variants &&
                variants.slice(0, 4).map(
                  (variant) => html`
                    <li>
                      <a
                        href="${variant.url}"
                        class="color-swatch with-image block relative"
                        title="${variant.titre}"
                        aria-label="${variant.titre}"
                        style="--swatch-background: x3;--swatch-background-image: url(${variant.image_url}&amp;width=64);"
                        ><span class="sr-only">${variant.titre}</span></a
                      >
                    </li>
                  `,
                )}
              </ul>
              ${variants &&
              variants.length > 4 &&
              html`
                <a
                  href="${algoliaShopify.helpers.instantsearchLink(hit)}"
                  class="reversed-link font-medium text-xs text-opacity leading-none tracking-widest"
                  is="hover-link"
                  aria-controls="Quickview-template__main-collection-${hit.handle}-${hit.objectID}"
                  aria-expanded="false"
                  >+${variants.length - 4}</a
                >
              `}
            `}
          </div>
        </div>

        ${product_specs.length > 0 && hasSpecs
          ? html`
              <div class="product-card__icons product-card__spec">
                <touch-slider class="touch-slider flex flex-nowrap">
                  ${product_specs.map((entry) => {
                    const [keyFirst, keyLast] = (entry.key || "")
                      .split(".")
                      .map((s) => s.trim());
                    let value = null;

                    if (keyFirst && keyLast) {
                      value = hit.meta?.[keyFirst]?.[keyLast];
                    }

                    if (value === undefined || value === null || value === "") {
                      return null; // Ignore les entrées sans valeur
                    }

                    const name = entry.name || "";
                    let iconUrl = getImageUrlFromGid(entry.icon);

                    const variantIcon = getIconVariant(value);
                    if (variantIcon) iconUrl = variantIcon;

                    const displayValue = cleanValue(value);
                    const translation =
                      value == "true"
                        ? algoliaShopify.translations.booleanYes
                        : algoliaShopify.translations.booleanNo;

                    if (value == "false") {
                      return;
                    }

                    return html`
                      <div class="touch-slider__item">
                        <div
                          class="product-card__icon flex gap-2 flex-row items-start"
                        >
                          ${iconUrl
                            ? html`<img
                                src="${iconUrl}"
                                class="product__spec--icon"
                              />`
                            : ""}
                          <div
                            class="flex flex-col gap-1 text-sm font-medium leading-none text-left items-start whitespace-nowrap"
                          >
                            ${value == "true"
                              ? html`
                                  ${name}
                                  <p
                                    class="text-opacity font-normal text-xs leading-tight whitespace-nowrap"
                                  >
                                    ${translation}
                                  </p>
                                `
                              : html`
                                  ${displayValue}
                                  ${name
                                    ? html`<p
                                        class="text-opacity font-normal text-xs leading-tight whitespace-nowrap"
                                      >
                                        ${name}
                                      </p>`
                                    : ""}
                                `}
                          </div>
                        </div>
                      </div>
                    `;
                  })}
                </touch-slider>
              </div>
            `
          : ""}
      </div>
    `;
  }
});
